import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Container,
    Spinner,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const LuckyDrawList = () => {
    const [draws, setDraws] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const toast = useRef(null);

    const breadcrumbItems = [
        { title: "Home", link: "/admin/dashboard" },
        { title: "Lucky Draw", link: "#!" },
    ];

    const fetchDraws = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/luckydraw/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            const paginator = response.data?.data;
            const list = Array.isArray(paginator?.data) ? paginator.data : [];
            setDraws(list);
            setTotalRecords(paginator?.total ?? list.length);
        } catch (error) {
            console.error("Error fetching lucky draws:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch lucky draw data",
                life: 3000,
            });
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchDraws();
    }, [fetchDraws]);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const dateBodyTemplate = (field) => (rowData) => {
        if (!rowData[field]) return "—";
        return new Date(rowData[field]).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const hasWebsiteTemplate = (rowData) => {
        const val = rowData.has_website;
        if (val === "1" || val === 1 || val === true) {
            return <span className="badge bg-success-subtle text-success">Yes</span>;
        }
        return <span className="badge bg-secondary-subtle text-secondary">No</span>;
    };

    const visibleData = draws.slice(first, first + rows);

    return (
        <React.Fragment>
            <Toast ref={toast} position="top-right" />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Lucky Draw" breadcrumbItems={breadcrumbItems} />
                    <Card className="shadow-sm">
                        <CardHeader
                            className="d-flex justify-content-between align-items-center"
                            style={{
                                background: "linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)",
                                borderRadius: "0.375rem 0.375rem 0 0",
                            }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <i className="ri-gift-line text-white fs-4"></i>
                                <h4 className="card-title mb-0 text-white">Lucky Draw List</h4>
                            </div>
                            <span className="badge bg-white text-dark px-3 py-2 fw-semibold">
                                {totalRecords} Total
                            </span>
                        </CardHeader>

                        <CardBody className="p-0">
                            {loading ? (
                                <div
                                    className="d-flex justify-content-center align-items-center"
                                    style={{ minHeight: "200px" }}
                                >
                                    <Spinner color="primary" />
                                </div>
                            ) : (
                                <>
                                    <DataTable
                                        value={visibleData}
                                        loading={loading}
                                        stripedRows
                                        className="border-0"
                                        emptyMessage={
                                            <div className="text-center py-5 text-muted">
                                                <i className="ri-gift-line fs-1 d-block mb-2"></i>
                                                No lucky draw records found
                                            </div>
                                        }
                                        pt={{
                                            headerRow: { className: "table-light" },
                                        }}
                                    >
                                        <Column
                                            header="#"
                                            body={(_, { rowIndex }) => (
                                                <span className="fw-semibold text-muted">
                                                    {rowIndex + 1 + first}
                                                </span>
                                            )}
                                            style={{ width: "60px" }}
                                        />
                                        <Column header="Company Name" field="company_name" sortable />
                                        <Column header="Email" field="email" sortable />
                                        <Column header="Contact Number" field="contact_number" sortable />
                                        <Column
                                            header="Has Website"
                                            body={hasWebsiteTemplate}
                                            style={{ width: "120px" }}
                                        />
                                        <Column
                                            header="Website URL"
                                            field="website_url"
                                            body={(rowData) =>
                                                rowData.website_url ? (
                                                    <a
                                                        href={
                                                            rowData.website_url.startsWith("http")
                                                                ? rowData.website_url
                                                                : `https://${rowData.website_url}`
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary"
                                                    >
                                                        {rowData.website_url}
                                                    </a>
                                                ) : (
                                                    "—"
                                                )
                                            }
                                        />
                                        <Column header="Address" field="address" sortable />
                                        <Column
                                            header="Note"
                                            field="note"
                                            body={(rowData) => (
                                                <span
                                                    className="text-truncate d-inline-block"
                                                    style={{ maxWidth: "180px" }}
                                                    title={rowData.note}
                                                >
                                                    {rowData.note || "—"}
                                                </span>
                                            )}
                                        />
                                        <Column
                                            header="Created At"
                                            body={dateBodyTemplate("created_at")}
                                            field="created_at"
                                            sortable
                                        />
                                    </DataTable>

                                    {totalRecords > rows && (
                                        <div className="px-3 pb-3">
                                            <Paginator
                                                first={first}
                                                rows={rows}
                                                totalRecords={totalRecords}
                                                onPageChange={onPageChange}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                className="mt-3"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default LuckyDrawList;
